import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get full URL for uploaded files
const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Helper to get mobile grid class based on mobileColumns setting
const getMobileGridClass = (mobileColumns) => {
  switch (mobileColumns) {
    case '2':
      return 'grid-cols-2';
    case '3':
      return 'grid-cols-3';
    default:
      return 'grid-cols-1';
  }
};

// Collapsible Card Description Component
const CollapsibleDescription = ({ description, textAlign, textColor, isCompact }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 100;
  const shouldCollapse = description && description.length > maxLength;
  
  const displayText = shouldCollapse && !isExpanded 
    ? description.substring(0, maxLength) + '...' 
    : description;

  return (
    <div style={{ textAlign: textAlign || 'center' }}>
      <p 
        className={`${isCompact ? 'text-xs md:text-lg' : 'text-lg'} whitespace-pre-line`}
        style={{ color: textColor }}
      >
        {displayText}
      </p>
      {shouldCollapse && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 text-sm font-medium flex items-center gap-1 mx-auto hover:opacity-80 transition-opacity"
          style={{ color: textColor, justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center' }}
        >
          {isExpanded ? (
            <>Згорнути <ChevronUp className="w-4 h-4" /></>
          ) : (
            <>Детальніше <ChevronDown className="w-4 h-4" /></>
          )}
        </button>
      )}
    </div>
  );
};

// Custom Card Component with collapsible description
const CustomCard = ({ item, section, titleColor, contentTextColor }) => {
  const isCompact = section.mobileColumns && section.mobileColumns !== '1';
  
  // Use individual card colors if set, otherwise fall back to section colors
  const cardTitleColor = item.titleColor || titleColor;
  const cardDescColor = item.descriptionColor || contentTextColor;
  
  return (
    <Card className="hover-card" data-testid={`custom-card-${item.id}`}>
      <CardContent 
        className={`${isCompact ? 'p-3 md:p-8' : 'p-8'}`}
        style={{ textAlign: item.textAlign || 'center' }}
      >
        {item.emoji && (
          <div className={`mb-4 ${isCompact ? 'text-3xl md:text-5xl' : 'text-5xl'}`}>
            {item.emoji}
          </div>
        )}
        {item.image && (
          <img 
            src={getMediaUrl(item.image)} 
            alt={item.title || "Gastro Bloggers Day"} 
            loading="lazy"
            decoding="async"
            className={`w-full object-cover rounded-lg mb-4 ${isCompact ? 'h-24 md:h-40' : 'h-40'}`} 
          />
        )}
        {item.title && (
          <h3 
            className={`font-bold mb-3 ${isCompact ? 'text-sm md:text-2xl' : 'text-2xl'}`} 
            style={{ color: cardTitleColor }}
          >
            {item.title}
          </h3>
        )}
        {item.description && (
          item.collapsible ? (
            <CollapsibleDescription 
              description={item.description}
              textAlign={item.textAlign}
              textColor={cardDescColor}
              isCompact={isCompact}
            />
          ) : (
            <p 
              className={`${isCompact ? 'text-xs md:text-lg' : 'text-lg'}`}
              style={{ color: cardDescColor, textAlign: item.textAlign || 'center' }}
            >
              {item.description}
            </p>
          )
        )}
        {item.showButton && item.buttonText && (
          <Button 
            asChild 
            className={`gold-gradient-btn mt-4 ${isCompact ? 'text-xs px-2 py-1 md:text-base md:px-4 md:py-2' : ''}`}
          >
            <a href={item.buttonLink} target="_blank" rel="noopener noreferrer">
              {item.buttonText}
            </a>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Render a single custom section
export const CustomSection = ({ section }) => {
  if (!section || !section.enabled) return null;

  const mobileGridClass = getMobileGridClass(section.mobileColumns);
  const titleColor = section.titleColor || section.textColor || '#712033';
  const contentTextColor = section.contentTextColor || '#4a5568';

  return (
    <section 
      className="custom-section py-20 px-6 md:px-12"
      style={{ backgroundColor: section.bgColor || '#ffffff' }}
      data-testid={`custom-section-${section.id}`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <h2 
          className="section-title text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ color: titleColor }}
        >
          {section.title}
        </h2>
        
        {/* Section Subtitle */}
        {section.subtitle && (
          <p className="text-lg text-center mb-16 -mt-12 opacity-80" style={{ color: titleColor }}>
            {section.subtitle}
          </p>
        )}
        
        {/* Section Content based on type */}
        {section.type === 'cards' && (
          <div className={`grid ${mobileGridClass} md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6`}>
            {section.content.filter(item => item.type === 'card').map((item) => (
              <CustomCard 
                key={item.id} 
                item={item} 
                section={section} 
                titleColor={titleColor}
                contentTextColor={contentTextColor}
              />
            ))}
          </div>
        )}
        
        {section.type === 'badges' && (
          <div className="flex flex-wrap justify-center gap-4">
            {section.content.filter(item => item.type === 'badge').map((item) => (
              <span 
                key={item.id} 
                className="px-6 py-3 bg-white rounded-full font-bold text-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2"
                style={{ color: item.textColor || titleColor }}
                data-testid={`custom-badge-${item.id}`}
              >
                {item.emoji && <span className="text-2xl">{item.emoji}</span>}
                {item.badgeText}
              </span>
            ))}
          </div>
        )}
        
        {section.type === 'text' && (
          <div className="space-y-6">
            {section.content.filter(item => item.type === 'text').map((item) => (
              <div key={item.id} style={{ textAlign: item.textAlign || 'center' }} data-testid={`custom-text-${item.id}`}>
                {item.title && (
                  <h3 className="text-2xl font-bold mb-3" style={{ color: item.titleColor || titleColor }}>
                    {item.title}
                  </h3>
                )}
                {item.description && (
                  <p className="text-lg whitespace-pre-line" style={{ color: item.descriptionColor || contentTextColor }}>
                    {item.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        
        {section.type === 'gallery' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {section.content.filter(item => item.type === 'image').map((item) => (
              <div key={item.id} className="group" data-testid={`custom-image-${item.id}`}>
                <img 
                  src={getMediaUrl(item.image)} 
                  alt={item.title || 'Gastro Bloggers Day'} 
                  loading="lazy"
                  decoding="async"
                  className="w-full h-48 object-cover rounded-lg shadow-md group-hover:shadow-xl transition-all duration-300"
                />
                {item.title && (
                  <p className="text-center mt-2 text-sm" style={{ color: section.textColor || '#712033' }}>
                    {item.title}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        
        {section.type === 'mixed' && (
          <div className="space-y-8">
            {/* Cards in mixed mode */}
            {section.content.filter(item => item.type === 'card').length > 0 && (
              <div className={`grid ${mobileGridClass} md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6`}>
                {section.content.filter(item => item.type === 'card').map((item) => (
                  <CustomCard 
                    key={item.id} 
                    item={item} 
                    section={section} 
                    titleColor={titleColor}
                    contentTextColor={contentTextColor}
                  />
                ))}
              </div>
            )}
            
            {/* Badges in mixed mode */}
            {section.content.filter(item => item.type === 'badge').length > 0 && (
              <div className="flex flex-wrap justify-center gap-4">
                {section.content.filter(item => item.type === 'badge').map((item) => (
                  <span 
                    key={item.id} 
                    className="px-6 py-3 bg-white rounded-full font-bold text-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2"
                    style={{ color: item.textColor || titleColor }}
                  >
                    {item.emoji && <span className="text-2xl">{item.emoji}</span>}
                    {item.badgeText}
                  </span>
                ))}
              </div>
            )}
            
            {/* Text in mixed mode */}
            {section.content.filter(item => item.type === 'text').map((item) => (
              <div key={item.id} style={{ textAlign: item.textAlign || 'center' }}>
                {item.title && (
                  <h3 className="text-2xl font-bold mb-3" style={{ color: item.titleColor || titleColor }}>
                    {item.title}
                  </h3>
                )}
                {item.description && (
                  <p className="text-lg whitespace-pre-line" style={{ color: item.descriptionColor || contentTextColor }}>
                    {item.description}
                  </p>
                )}
              </div>
            ))}
            
            {/* Images in mixed mode */}
            {section.content.filter(item => item.type === 'image').length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {section.content.filter(item => item.type === 'image').map((item) => (
                  <div key={item.id} className="group">
                    <img 
                      src={getMediaUrl(item.image)} 
                      alt={item.title || 'Gastro Bloggers Day'} 
                      loading="lazy"
                      decoding="async"
                      className="w-full h-48 object-cover rounded-lg shadow-md group-hover:shadow-xl transition-all duration-300"
                    />
                    {item.title && (
                      <p className="text-center mt-2 text-sm" style={{ color: section.textColor || '#712033' }}>
                        {item.title}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// Get custom sections for a specific position
export const getCustomSectionsForPosition = (customSections, position) => {
  if (!customSections || !Array.isArray(customSections)) return [];
  return customSections.filter(s => s.enabled && (s.position || 'after-organizers') === position);
};

// Render all custom sections for a given position
export const CustomSectionsAtPosition = ({ customSections, position }) => {
  const sections = getCustomSectionsForPosition(customSections, position);
  if (sections.length === 0) return null;
  
  return (
    <>
      {sections.map(section => (
        <CustomSection key={section.id} section={section} />
      ))}
    </>
  );
};

export default CustomSection;
